// The app: route table, guards and the shell each page sits in.
import { brand } from './lib/brand.js';
import { html, useEffect, useState } from './lib/html.js';
import { loc, match, navigate } from './lib/router.js';
import { lazy } from './lib/lazy.js';
import { authResolved, isSignedIn, isAdminOf, refreshClaims, user } from './lib/firebase.js';
import { setAccount, accountId } from './lib/account.js';
import { sessionLost } from './lib/api.js';
import { t } from './lib/i18n.js';
import { DialogHost, ToastHost, Loading } from './ui/overlay.js';
import { AppShell } from './shell/app-shell.js';

const Home = lazy(() => import('./pages/home.js'), 'HomePage');
const StaticPage = lazy(() => import('./pages/static.js'), 'StaticPage');
const Pricing = lazy(() => import('./pages/static.js'), 'PricingPage');
const Contact = lazy(() => import('./pages/static.js'), 'ContactPage');
const Login = lazy(() => import('./pages/login.js'), 'LoginPage');
const SignUp = lazy(() => import('./pages/signup.js'), 'SignUpPage');
const Booking = lazy(() => import('./pages/booking.js'), 'BookingPage');
const Appointments = lazy(() => import('./pages/appointments.js'), 'AppointmentsPage');
const Profile = lazy(() => import('./pages/profile.js'), 'ProfilePage');
const Help = lazy(() => import('./pages/help.js'), 'HelpPage');
const BookingsOverview = lazy(() => import('./pages/bookings-overview.js'), 'BookingsOverviewPage');
const OAuthAuthorize = lazy(() => import('./pages/oauth-authorize.js'), 'OAuthAuthorizePage');
const Integration = lazy(() => import('./pages/integration.js'), 'IntegrationPage');
const Admin = lazy(() => import('./admin/admin.js'), 'AdminRoutes');
const Owner = lazy(() => import('./owner/owner.js'), 'OwnerPage');

// shell: 'site' = public marketing pages (own header/footer), 'app' = the signed-in app with its
// navigation, 'bare' = nothing around it.
const routes = [
  { path: '/', component: Home, shell: 'bare' },
  { path: '/login', component: Login, shell: 'bare' },
  { path: '/signup', component: SignUp, shell: 'bare' },
  { path: '/about', component: StaticPage, props: { page: 'about' }, shell: 'bare' },
  { path: '/contact', component: Contact, shell: 'bare' },
  { path: '/privacy', component: StaticPage, props: { page: 'privacy' }, shell: 'bare' },
  { path: '/terms', component: StaticPage, props: { page: 'terms' }, shell: 'bare' },
  { path: '/data-processing', component: StaticPage, props: { page: 'dpa' }, shell: 'bare' },
  { path: '/pricing', component: Pricing, shell: 'bare' },
  { path: '/bookings', component: BookingsOverview, guard: 'auth', shell: 'app' },
  { path: '/oauth/authorize', component: OAuthAuthorize, guard: 'auth', shell: 'bare' },
  { path: '/integration', component: Integration, shell: 'bare' },
  { path: '/integration/*', component: Integration, shell: 'bare' },
  { path: '/admin', component: Owner, shell: 'bare' },
  { path: '/admin/*', component: Owner, shell: 'bare' },
  { path: '/:accountId', redirect: (p) => `/${p.accountId}/book` },
  { path: '/:accountId/book', component: Booking, shell: 'app', account: true },
  { path: '/:accountId/appointments', component: Appointments, guard: 'auth', shell: 'app', account: true },
  { path: '/:accountId/profile', component: Profile, shell: 'app', account: true },
  { path: '/:accountId/help', component: Help, guard: 'auth', shell: 'app', account: true },
  { path: '/:accountId/login', component: Login, shell: 'bare', account: true },
  { path: '/:accountId/admin', redirect: (p) => `/${p.accountId}/admin/calendars` },
  { path: '/:accountId/admin/settings', redirect: (p) => `/${p.accountId}/admin/settings/consultants` },
  { path: '/:accountId/admin/*', component: Admin, guard: 'admin', shell: 'app', account: true }
];

// booking.norplate.com's sales pages are Norplate's (Norplate Booking is bought there): index.html sends a fresh
// visit on before anything paints, and this catches a click inside the app (the wordmark's link to /)
const NORPLATE_SALES = { '/': '/', '/pricing': '/#pris', '/about': '/', '/contact': '/#kontakt', '/signup': '/kom-i-gang' };
const ToNorplate = ({ to }) => {
  useEffect(() => location.replace(`https://norplate.com${to}`), []);
  return null;
};
if (location.hostname === 'booking.norplate.com') {
  for (const route of routes) {
    if (route.path in NORPLATE_SALES) Object.assign(route, { component: ToNorplate, props: { to: NORPLATE_SALES[route.path] } });
  }
}

function resolve(path) {
  for (const route of routes) {
    const params = match(route.path, path);
    if (params) return { route, params };
  }
  return null;
}

/** Where someone lands on an account when nothing more specific was asked for. */
export function defaultRoute(acc) {
  if (!isSignedIn.value) return `/${acc}/book`;
  return isAdminOf(acc) ? `/${acc}/admin` : `/${acc}/appointments`;
}

// One forced claims refresh per account before turning an admin away: a claim granted while
// they were signed in only reaches their token on its next refresh.
const refreshedFor = new Set();

function useGuard(route, params) {
  const [state, setState] = useState('checking');
  const signedIn = isSignedIn.value;
  const resolved = authResolved.value;
  const acc = params?.accountId;
  useEffect(() => {
    if (!route.guard) return void setState('ok');
    if (!resolved) return void setState('checking');
    if (route.guard === 'auth') {
      if (signedIn) return void setState('ok');
      setState('denied');
      const here = location.pathname + location.search;
      navigate(acc ? `/${acc}/login?redir=${encodeURIComponent(here)}` : `/login?redir=${encodeURIComponent(here)}`, { replace: true });
      return;
    }
    if (route.guard === 'admin') {
      if (isAdminOf(acc)) return void setState('ok');
      if (signedIn && !refreshedFor.has(acc)) {
        refreshedFor.add(acc);
        setState('checking');
        refreshClaims().then(() => {
          if (isAdminOf(acc)) setState('ok');
          else {
            setState('denied');
            navigate(`/${acc}/login`, { replace: true });
          }
        });
        return;
      }
      setState('denied');
      const here = location.pathname + location.search;
      navigate(`/${acc}/login${signedIn ? '' : `?redir=${encodeURIComponent(here)}`}`, { replace: true });
    }
  }, [route, acc, resolved, signedIn, user.value?.uid]);
  return state;
}

export function App() {
  const { path } = loc.value;
  const hit = resolve(path);

  useEffect(() => {
    if (hit?.route.redirect) navigate(hit.route.redirect(hit.params) + location.search + location.hash, { replace: true });
  }, [path]);

  // The account follows the URL; pages outside an account keep the last one for the nav.
  const acc = hit?.route.account ? hit.params.accountId : null;
  if (acc && acc !== accountId.value) setAccount(acc);

  // A request found the session gone: back to sign in, on this account if there is one.
  useEffect(() => {
    if (!sessionLost.value) return;
    navigate(accountId.value ? `/${accountId.value}/login` : '/login', { replace: true });
  }, [sessionLost.value]);

  useEffect(() => {
    document.title = t('app.title') || brand.name;
  }, [path]);

  return html`
    ${hit && !hit.route.redirect ? html`<${Routed} key=${hit.route.path + '|' + (hit.params.accountId || '')} route=${hit.route} params=${hit.params} />` : !hit ? html`<${NotFound} />` : null}
    <${DialogHost} />
    <${ToastHost} />
  `;
}

function Routed({ route, params }) {
  const guard = useGuard(route, params);
  const Page = route.component;
  const content = guard === 'ok' ? html`<${Page} ...${route.props} params=${params} />` : html`<${Loading} />`;
  if (route.shell === 'app') return html`<${AppShell} accountId=${params.accountId}>${content}</${AppShell}>`;
  return content;
}

function NotFound() {
  useEffect(() => navigate('/', { replace: true }), []);
  return null;
}
